import { Trans, t } from "@lingui/macro";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Footer from "components/Footer/Footer";
import Tab from "components/Tab/Tab";

import { useCopyTradingFollowing, useCopyTradingLeaderboard } from "domain/copyTrading/useCopyTradingData";
import { followTrader, unfollowTrader } from "domain/copyTrading/contracts";
import type { LeaderboardTab, TraderProfile } from "domain/copyTrading/types";

import { useChainId } from "lib/chains";
import { parseValue } from "lib/numbers";
import { helperToast } from "lib/helperToast";
import { usePendingTxns } from "lib/usePendingTxns";
import useWallet from "lib/wallets/useWallet";
import { useEthersSigner } from "lib/wallets/useEthersSigner";

import { FollowModal } from "./components/FollowModal";
import { RegisterTraderModal } from "./components/RegisterTraderModal";
import { ProfileEditModal } from "./components/ProfileEditModal";
import { StatsHero } from "./components/StatsHero";
import { TraderLeaderboard } from "./components/TraderLeaderboard";

import "./CopyTradingPage.scss";

const TABS: LeaderboardTab[] = ["topTraders", "mostCopied", "rising", "myFollowing"];

const TAB_LABELS: Record<LeaderboardTab, string> = {
  topTraders: "Top Traders",
  mostCopied: "Most Copied",
  rising: "Rising Stars",
  myFollowing: "My Following",
};

const TAB_SORT: Record<LeaderboardTab, string> = {
  topTraders: "pnl",
  mostCopied: "followers",
  rising: "followers",
  myFollowing: "followers",
};

function filterByTab(traders: TraderProfile[], tab: LeaderboardTab, followingSet: Set<string>): TraderProfile[] {
  if (tab === "myFollowing") {
    return traders.filter((t) => followingSet.has(t.address.toLowerCase()));
  }
  if (tab === "rising") {
    return [...traders].sort((a, b) => {
      const scoreA = (a.winRate ?? 0) * 1.5 - a.followerCount * 0.01;
      const scoreB = (b.winRate ?? 0) * 1.5 - b.followerCount * 0.01;
      return scoreB - scoreA;
    });
  }
  return traders;
}

export function CopyTradingPage() {
  const { account } = useWallet();
  const { chainId } = useChainId();
  const signer = useEthersSigner({ chainId });
  const [, setPendingTxns] = usePendingTxns();

  const [activeTab, setActiveTab] = useState<LeaderboardTab>("topTraders");
  const [followModal, setFollowModal] = useState<TraderProfile | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const leaderboardState = useCopyTradingLeaderboard(TAB_SORT[activeTab]);
  const followingState = useCopyTradingFollowing(account ?? undefined);

  const allTraders = leaderboardState.status === "success" ? leaderboardState.data : [];
  const followingList = followingState.status === "success" ? followingState.data : [];
  const followingSet = useMemo(
    () => new Set(followingList.map((r) => r.trader.address.toLowerCase())),
    [followingList]
  );

  const visibleTraders = useMemo(
    () => filterByTab(allTraders, activeTab, followingSet),
    [allTraders, activeTab, followingSet]
  );

  // Already a registered leader? Then don't offer "Become a Leader" again.
  const myProfile = useMemo(
    () => (account ? allTraders.find((t) => t.address.toLowerCase() === account.toLowerCase()) : undefined),
    [account, allTraders]
  );
  const isRegisteredLeader = Boolean(myProfile);

  const handleFollow = useCallback((trader: TraderProfile) => {
    setFollowModal(trader);
  }, []);

  const handleUnfollow = useCallback(
    async (address: string) => {
      if (!signer) {
        helperToast.error(t`Connect your wallet to unfollow.`);
        return;
      }
      try {
        await unfollowTrader({ chainId, signer, traderAddress: address, setPendingTxns });
      } catch {
        // callContract already shows error toast
      }
    },
    [chainId, signer, setPendingTxns]
  );

  const handleConfirmFollow = useCallback(
    async (trader: TraderProfile, collateralPerTradeStr: string, maxAllocationStr: string) => {
      if (!signer) {
        helperToast.error(t`Connect your wallet to follow.`);
        return;
      }
      setIsSubmitting(true);
      try {
        // Collateral is denominated in the stable collateral token, BUSDC (18 decimals).
        // parseValue uses ethers.parseUnits so large 18-decimal amounts stay exact.
        const toCollateral = (s: string) => parseValue(s || "0", 18) ?? 0n;
        await followTrader({
          chainId,
          signer,
          traderAddress: trader.address,
          collateralPerTrade: toCollateral(collateralPerTradeStr),
          maxTotalAllocation: toCollateral(maxAllocationStr),
          setPendingTxns,
        });
        setFollowModal(null);
      } catch {
        // callContract already shows error toast
      } finally {
        setIsSubmitting(false);
      }
    },
    [chainId, signer, setPendingTxns]
  );

  const isLoading = leaderboardState.status === "loading";

  return (
    <div className="page-layout">
      <div className="default-container CT-page">
        {/* Page header */}
        <div className="CT-header">
          <div className="CT-header__title">
            <h1 className="CT-header__heading">
              <Trans>Copy Trading</Trans>
            </h1>
            <p className="CT-header__sub">
              <Trans>Follow top traders and automatically mirror their positions. Leaders earn a fee when you profit.</Trans>
            </p>
          </div>
          {account && (
            <div className="CT-header-actions">
              {isRegisteredLeader ? (
                <button className="CT-btn-secondary" onClick={() => setShowProfileModal(true)}>
                  <Trans>Edit Profile</Trans>
                </button>
              ) : (
                <button className="CT-btn-secondary" onClick={() => setShowRegisterModal(true)}>
                  <Trans>Become a Leader</Trans>
                </button>
              )}
              <Link to="/copytrading/portfolio" className="CT-btn-secondary">
                <Trans>My Portfolio →</Trans>
              </Link>
            </div>
          )}
        </div>

        {/* Hero stats */}
        <StatsHero traders={allTraders} followingCount={followingList.length} />

        {/* Tab navigation */}
        <div className="CT-tabs-wrap">
          <Tab
            options={TABS}
            option={activeTab}
            setOption={(v) => setActiveTab(v as LeaderboardTab)}
            optionLabels={TAB_LABELS}
            type="block"
            className="CT-tabs"
          />
        </div>

        {/* My Following empty state */}
        {activeTab === "myFollowing" && visibleTraders.length === 0 && !isLoading && account && (
          <div className="CT-empty-state">
            <div className="CT-empty-state__icon">📈</div>
            <h3>
              <Trans>You're not following anyone yet</Trans>
            </h3>
            <p>
              <Trans>Browse Top Traders and click Follow to start copy trading automatically.</Trans>
            </p>
            <button className="CT-btn-primary" onClick={() => setActiveTab("topTraders")}>
              <Trans>Explore Top Traders</Trans>
            </button>
          </div>
        )}

        {/* Not connected state for My Following */}
        {activeTab === "myFollowing" && !account && (
          <div className="CT-empty-state">
            <div className="CT-empty-state__icon">🔗</div>
            <h3>
              <Trans>Connect your wallet</Trans>
            </h3>
            <p>
              <Trans>Connect your wallet to view and manage your copy trading portfolio.</Trans>
            </p>
          </div>
        )}

        {/* Leaderboard table */}
        {(activeTab !== "myFollowing" || (account && visibleTraders.length > 0)) && (
          <TraderLeaderboard
            traders={visibleTraders}
            isLoading={isLoading}
            followingSet={followingSet}
            onFollow={handleFollow}
            onUnfollow={handleUnfollow}
            currentAccount={account ?? undefined}
          />
        )}

        {/* Error state */}
        {leaderboardState.status === "error" && (
          <div className="CT-error-banner">
            <Trans>Failed to load traders — please try again later.</Trans>
          </div>
        )}
      </div>

      <FollowModal
        isVisible={followModal !== null}
        trader={followModal}
        onClose={() => setFollowModal(null)}
        onConfirm={handleConfirmFollow}
        isSubmitting={isSubmitting}
      />

      <RegisterTraderModal
        isVisible={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
      />

      <ProfileEditModal
        isVisible={showProfileModal}
        address={account ?? ""}
        signer={signer}
        currentName={myProfile?.displayName}
        currentAvatarUrl={myProfile?.avatarUrl}
        onClose={() => setShowProfileModal(false)}
        onSaved={() => {
          // The leaderboard poll (20s) picks up the new name/photo; nothing else to do.
        }}
      />

      <Footer />
    </div>
  );
}
